import type { CharacterDto, ProjectDto, PromptContext } from '@studio/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { api, errorMessage, type RequestOptions } from '@/lib/api';

/** Everything the prompt builder needs, built in the browser so the prompt updates while typing. */
export function usePromptContext(project: ProjectDto, characters: CharacterDto[] | undefined): PromptContext {
  return useMemo(() => {
    const chars = new Map<string, CharacterDto>();
    for (const c of characters ?? []) chars.set(c.id, c);
    for (const c of project.cast) chars.set(c.id, c);
    return {
      style: project.style,
      language: project.language,
      aspect: project.aspect,
      castIds: project.castIds,
      characters: Object.fromEntries(
        [...chars.values()].map((c) => [
          c.id,
          {
            id: c.id,
            name: c.name,
            callName: c.callName,
            visualDescription: c.visualDescription,
            voiceDescription: c.voiceDescription,
          },
        ]),
      ),
      products: project.product
        ? {
            [project.product.id]: {
              id: project.product.id,
              title: project.product.title,
              promptDescription: project.product.promptDescription,
            },
          }
        : {},
    };
  }, [project.style, project.language, project.aspect, project.castIds, project.cast, project.product, characters]);
}

/** Calls an endpoint that answers with the whole project, and puts that answer in the cache. */
export function useProjectCall(projectId: string) {
  const qc = useQueryClient();
  return useCallback(
    async <T = ProjectDto,>(
      path: string,
      opts: RequestOptions = {},
      pick?: (r: T) => ProjectDto,
    ): Promise<T | null> => {
      try {
        const res = await api<T>(path, opts);
        const project = pick ? pick(res) : (res as unknown as ProjectDto);
        if (project?.id === projectId) qc.setQueryData(['project', projectId], project);
        void qc.invalidateQueries({ queryKey: ['gpu'] });
        return res;
      } catch (err) {
        toast.error(errorMessage(err));
        return null;
      }
    },
    [qc, projectId],
  );
}

export function SectionTitle({ children, hint }: { children: React.ReactNode; hint?: React.ReactNode }) {
  return (
    <div className="mb-2 mt-5 first:mt-0">
      <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{children}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}
